import React, { useEffect, useMemo, useState } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { useFamilyStore } from '../../stores/familyStore'
import { useUserPreferencesStore } from '../../stores/userPreferencesStore'
import { getColorScheme } from '../../utils/colorSchemes'
import { getTypeColor } from '../../utils/eventTypeColors'
import { useEntitlements } from '../../hooks/useEntitlements'
import { usePermissions } from '../../hooks/usePermissions'
import { useCalendar } from '../../hooks/useCalendar'
import { useEventsStore } from '../../stores/eventsStore'
import { useCalendarStore } from '../../stores/calendarStore'
import { NOTE_DURATION_OPTIONS, formatNoteDuration } from './noteDurations'

type CombinedMode = 'care' | 'note' | 'expense'

interface CombinedModalProps {
  isOpen: boolean
  onClose: () => void
  defaultDate: Date
  childId?: string
}

// Modal łączący: Opieka / Notatka / Koszt
export const CombinedModal: React.FC<CombinedModalProps> = ({ isOpen, onClose, defaultDate, childId }) => {
  const { user } = useAuthStore()
  const { family, members } = useFamilyStore()
  const scheme = useUserPreferencesStore(state => getColorScheme(state.preferences.color_scheme))
  const { plan: entitlements } = useEntitlements(family?.id)
  const { canEditCalendar, isObserver } = usePermissions()
  const { addEvent } = useEventsStore()
  const { updateCalendarEntry } = useCalendar()
  const entries = useCalendarStore(state => state.entries)

  const [mode, setMode] = useState<CombinedMode>('care')

  // Opieka – lokalny wybór nowej lokalizacji
  const dateKey = useMemo(() => {
    const d = new Date(defaultDate)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [defaultDate])
  const existingEntry = useMemo(() => entries.find(e => e.date === dateKey), [entries, dateKey])
  const [careSelection, setCareSelection] = useState<'home_a'|'home_b'|null>(existingEntry?.location ?? null)

  // Notatka/Koszt – pola formularza (adaptacja z EventModal)
  const toLocalInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0,16)
  const fromLocalInput = (s: string) => new Date(s)
  const defaultNoon = (date: Date) => {
    const d = new Date(date)
    d.setHours(12, 0, 0, 0)
    return d
  }
  const [title, setTitle] = useState('')
  const [noteAllDay, setNoteAllDay] = useState(false)
  const [startAt, setStartAt] = useState(() => toLocalInput(defaultNoon(defaultDate)))
  const [endAt, setEndAt] = useState(() => toLocalInput(new Date(defaultNoon(defaultDate).getTime() + 60*60*1000)))
  const [durationMinutes, setDurationMinutes] = useState<number>(60)
  const [location, setLocation] = useState('')
  const [notes, setNotes] = useState('')
  const [amount, setAmount] = useState<number | ''>('')
  const [currency, setCurrency] = useState('PLN')
  const [parentsOnly, setParentsOnly] = useState(false)
  const [saving, setSaving] = useState(false)

  // Resetuj formularz przy otwarciu
  useEffect(() => {
    if (isOpen) {
      setMode('note')
      setCareSelection(existingEntry?.location ?? null)
      const start = defaultNoon(defaultDate)
      setStartAt(toLocalInput(start))
      setEndAt(toLocalInput(new Date(start.getTime() + 60*60*1000)))
      setDurationMinutes(60)
      setTitle('')
      setLocation('')
      setNotes('')
      setAmount('')
      setCurrency('PLN')
      setNoteAllDay(false)
      setParentsOnly(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultDate])

  // Synchronizuj endAt z duration gdy tryb note i nie całodniowe
  useEffect(() => {
    if (mode === 'note' && !noteAllDay && startAt && durationMinutes) {
      const startDate = fromLocalInput(startAt)
      const newEnd = new Date(startDate.getTime() + durationMinutes * 60000)
      setEndAt(toLocalInput(newEnd))
    }
  }, [mode, noteAllDay, startAt, durationMinutes])

  if (!isOpen) return null

  const disabledEventsAdd = !entitlements.canAddEvents || isObserver || !user || !family || !childId || childId === 'all-children'
  const disabledCare = !canEditCalendar

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !family) return

    setSaving(true)
    try {
      if (mode === 'care') {
        // Zmień przypisanie opieki na wskazany dzień
        await updateCalendarEntry(dateKey, 'day', careSelection)
        onClose()
        return
      }

      if (mode === 'expense') {
        if (disabledEventsAdd) return
        if (amount === '' || Number(amount) <= 0) { setSaving(false); return }
        await addEvent({
          family_id: family.id,
          child_id: childId!,
          title: title.trim() || 'Wydatek',
          notes: notes?.trim() || null,
          start_at: fromLocalInput(startAt).toISOString() as any,
          end_at: null as any,
          all_day: false,
          location: location?.trim() || null,
          type: 'expense',
          amount: Number(amount),
          currency,
          metadata: {} as any,
          created_by: user.id,
          parents_only: parentsOnly,
        } as any)
        onClose()
        return
      }

      // note
      if (disabledEventsAdd) return
      await addEvent({
        family_id: family.id,
        child_id: childId!,
        title: title.trim() || 'Notatka',
        notes: notes?.trim() || null,
        start_at: fromLocalInput(startAt).toISOString() as any,
        end_at: noteAllDay ? null as any : fromLocalInput(endAt).toISOString() as any,
        all_day: noteAllDay,
        location: location?.trim() || null,
        type: 'note',
        amount: null as any,
        currency: 'PLN' as const,
        metadata: {} as any,
        created_by: user.id,
        parents_only: parentsOnly,
      } as any)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const myMember = members.find(mb => mb.user_id === user?.id)
  const myHome = myMember?.assigned_home || (myMember?.role === 'parent_a' ? 'home_a' : 'home_b')
  const noteColor = myHome === 'home_b' ? scheme.homeB.color : scheme.homeA.color
  const modalBorderColor = mode === 'care' ? '#0ea5e9' : mode === 'note' ? noteColor : getTypeColor('expense', scheme.id)

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-2 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border mt-[10vh] transition-colors duration-200" style={{ borderColor: modalBorderColor }}>
        {/* Top actions */}
        <div className="flex items-center justify-end px-4 pt-3">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm font-medium rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors mr-2">Anuluj</button>
          <button type="submit" form="combined-form" disabled={saving || (mode !== 'care' && disabledEventsAdd) || (mode==='care' && disabledCare)} title={(mode!=='care' && disabledEventsAdd) ? 'Dostępne w planach Pro/Max lub wybierz dziecko' : (mode==='care' && disabledCare ? 'Brak uprawnień do edycji kalendarza' : undefined)} className="btn btn-sm btn-primary disabled:opacity-60">{saving ? 'Zapisywanie...' : (mode === 'care' ? 'Zmień' : 'Dodaj')}</button>
        </div>

        {/* Mode switch */}
        <div className="px-4 pb-3 mt-3 border-b">
          <div className="grid grid-cols-3 gap-2">
            {(['care','note','expense'] as const).map(m => {
              const active = mode === m
              const color = m==='care' ? '#0ea5e9' : m==='note' ? noteColor : getTypeColor('expense', scheme.id)
              const label = m==='care' ? 'Opieka' : m==='note' ? 'Notatka' : 'Koszt'
              const gated = (m === 'note' || m === 'expense') && !entitlements.canAddEvents
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`relative w-full py-2 text-sm font-medium rounded-lg transition-all duration-200 ${active ? 'text-white shadow-sm border-2' : 'border border-gray-200'}`}
                  style={{
                    backgroundColor: active ? color : `${color}15`,
                    borderColor: active ? color : undefined,
                    color: active ? undefined : color,
                  }}
                >
                  {label}
                  {gated && (
                    <span className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500 text-white border border-sky-600">Pro/Max</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <form id="combined-form" onSubmit={handleSubmit} className="px-4 py-3">
          {mode === 'care' ? (
            <div className="space-y-3">
              <div className="text-sm text-slate-700">Dzień: {defaultDate.toLocaleDateString('pl-PL')}</div>
              <div className="flex items-stretch gap-2 justify-start">
                <button
                  type="button"
                  onClick={() => setCareSelection('home_a')}
                  className={`w-1/3 px-3 py-2 rounded-lg text-sm font-medium border text-white ${careSelection==='home_a' ? 'shadow-md border-2 ring-2 ring-blue-900' : 'opacity-60 hover:opacity-80 border-white/30'}`}
                  style={{ backgroundColor: getColorScheme(useUserPreferencesStore.getState().preferences.color_scheme).homeA.color, borderColor: careSelection==='home_a' ? getColorScheme(useUserPreferencesStore.getState().preferences.color_scheme).homeA.color : undefined }}
                >
                  {family?.home_a_name || 'Dom A'}
                </button>
                <button
                  type="button"
                  onClick={() => setCareSelection('home_b')}
                  className={`w-1/3 px-3 py-2 rounded-lg text-sm font-medium border text-white ${careSelection==='home_b' ? 'shadow-md border-2 ring-2 ring-blue-900' : 'opacity-60 hover:opacity-80 border-white/30'}`}
                  style={{ backgroundColor: getColorScheme(useUserPreferencesStore.getState().preferences.color_scheme).homeB.color, borderColor: careSelection==='home_b' ? getColorScheme(useUserPreferencesStore.getState().preferences.color_scheme).homeB.color : undefined }}
                >
                  {family?.home_b_name || 'Dom B'}
                </button>
                <button type="button" onClick={() => setCareSelection(null)} className={`w-1/3 px-3 py-2 rounded-lg text-sm font-medium border ${careSelection===null ? 'border-blue-600 ring-2 ring-blue-400' : 'border-slate-300 text-slate-700'} `}>
                  Nieprzypisane
                </button>
              </div>
              {!canEditCalendar && (
                <div className="mt-1 p-2 rounded bg-slate-100 border text-xs text-slate-600">Brak uprawnień do zmiany opieki.</div>
              )}
            </div>
          ) : mode === 'note' ? (
            <div>
              <div>
                <label className="block text-xs font-medium mb-1 text-slate-700">Tytuł</label>
                <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-slate-700">Od</label>
                  <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs md:text-sm leading-tight" required />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-slate-700">Czas trwania</label>
                  <select value={durationMinutes} onChange={e => setDurationMinutes(Number(e.target.value))} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" disabled={noteAllDay} required={!noteAllDay}>
                    {NOTE_DURATION_OPTIONS.map(v => <option key={v} value={v}>{formatNoteDuration(v)}</option>)}
                  </select>
                </div>
              </div>
              <label htmlFor="allday" className="flex items-center gap-2.5 mt-3 py-1.5 cursor-pointer">
                <input id="allday" type="checkbox" checked={noteAllDay} onChange={e => setNoteAllDay(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-blue-400" />
                <span className="text-sm text-slate-700">Cały dzień</span>
              </label>
              <label htmlFor="parentsOnlyCombined" className="flex items-center gap-2.5 py-1.5 cursor-pointer">
                <input id="parentsOnlyCombined" type="checkbox" checked={parentsOnly} onChange={e => setParentsOnly(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-blue-400" />
                <span className="text-sm text-slate-700">Widoczne tylko dla rodziców</span>
              </label>
              <div className="mt-3">
                <label className="block text-xs font-medium mb-1 text-slate-700">Miejsce</label>
                <input value={location} onChange={e => setLocation(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium mb-1 text-slate-700">Notatka</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
            </div>
          ) : (
            <div>
              <div>
                <label className="block text-xs font-medium mb-1 text-slate-700">Tytuł</label>
                <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-slate-700">Data</label>
                  <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs md:text-sm leading-tight" required />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-slate-700">Kwota</label>
                  <div className="flex gap-2">
                    <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
                    <select value={currency} onChange={e => setCurrency(e.target.value)} className="px-2 py-2 rounded-md border border-slate-300 focus:outline-none">
                      <option value="PLN">PLN</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>
              </div>
              <label htmlFor="parentsOnlyExpCombined" className="flex items-center gap-2.5 mt-3 py-1.5 cursor-pointer">
                <input id="parentsOnlyExpCombined" type="checkbox" checked={parentsOnly} onChange={e => setParentsOnly(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-blue-400" />
                <span className="text-sm text-slate-700">Widoczne tylko dla rodziców</span>
              </label>
              <div className="mt-3">
                <label className="block text-xs font-medium mb-1 text-slate-700">Notatka</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default CombinedModal


